import { createAsyncThunk } from '@reduxjs/toolkit';
import { UpsertTournamentTeamDto } from 'modules/teams/domain/dtos/upsert-tournament-team.dto';
import { GetTournamentTeams } from 'modules/teams/domain/interfaces/request-dto';
import { Team } from 'modules/teams/domain/interfaces/team';
import teamsRepository from 'modules/teams/domain/repositories/teams.repository';
import teamsResource from 'modules/teams/domain/resources/teams-resource';
import { baseApi } from 'shared/api/base-api';
import { getNormalizedDate } from 'shared/utils/get-normalized-date';
import { RootState } from 'store/store';

const PREFIX = 'teams';

/** Сбрасывает кэш карточки команды — мутации здесь thunk'и, тег сам не инвалидируется. */
const invalidateTeam = (teamId: number) => baseApi.util.invalidateTags([{ type: 'Team' as const, id: teamId }]);

export const getTournamentTeams = createAsyncThunk<{ teams: Team[]; tournamentId: number }, Omit<GetTournamentTeams, 'tournamentId'>>(
  `${PREFIX}/fetchTeams`,
  async (payload, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState() as RootState;
      const activeTournamentId = auth.activeTournamentId;

      if (!activeTournamentId) {
        throw new Error('No active tournament');
      }

      const teams = await teamsRepository.getTournamentTeams({ ...payload, tournamentId: activeTournamentId });
      return {
        teams,
        tournamentId: activeTournamentId,
      };
    } catch (error) {
      console.error(`${PREFIX}/fetchTeams error:`, error);

      let message;
      if (error instanceof Error) message = error.message;
      else message = String(error);
      return rejectWithValue(message);
    }
  },
);

export const getTournamentTeamsByFilter = createAsyncThunk<Team[], Omit<GetTournamentTeams, 'tournamentId' | 'offset'>>(
  `${PREFIX}/fetchTeamsByFilter`,
  async (payload, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState() as RootState;
      const activeTournamentId = auth.activeTournamentId;

      if (!activeTournamentId) {
        throw new Error('No active tournament');
      }

      return await teamsRepository.getTournamentTeams({ ...payload, offset: 0, tournamentId: activeTournamentId });
    } catch (error) {
      console.error(`${PREFIX}/fetchTeamsByFilter error:`, error);

      let message;
      if (error instanceof Error) message = error.message;
      else message = String(error);
      return rejectWithValue(message);
    }
  },
);

export const createTeam = createAsyncThunk<Team, UpsertTournamentTeamDto>(
  `${PREFIX}/createTeam`,
  async (payload, { rejectWithValue }) => {
    try {
      const team = { name: payload.name, shortName: payload.shortName, tournamentId: payload.tournamentId };
      const teamData = await teamsRepository.upsertTeam(team);
      const teamId = teamData.id;

      if (payload.logo) {
        const logo: File = Array.isArray(payload.logo) ? payload.logo[0] : payload.logo;
        const res = await teamsResource.uploadLogo(logo, teamId);
        teamData.logo = { id: res.id, url: res.emblem };
      }

      teamData.createdAt = getNormalizedDate(new Date());

      return teamData;
    } catch (error) {
      console.error(`${PREFIX}/createTeam error:`, error);

      let message;
      if (error instanceof Error) message = error.message;
      else message = String(error);
      return rejectWithValue(message);
    }
  },
);

export const deleteTeam = createAsyncThunk<{ id: number }, { id: number }>(
  `${PREFIX}/deleteTeam`,
  async (payload, { rejectWithValue }) => {
    try {
      await teamsRepository.deleteTeam(payload.id);
      return { id: payload.id };
    } catch (error) {
      console.error(`${PREFIX}/deleteTeam error:`, error);

      let message;
      if (error instanceof Error) message = error.message;
      else message = String(error);
      return rejectWithValue(message);
    }
  },
);

export const upsertTeamKits = createAsyncThunk<
  Team,
  { teamId: number; kits: Array<{ type: string; elements: Array<{ element: string; pattern: string; colors: string[] }> }> }
>(`${PREFIX}/upsertTeamKits`, async (payload, { rejectWithValue, dispatch }) => {
  try {
    const team = await teamsRepository.upsertTeamKits(payload.teamId, payload.kits);
    dispatch(invalidateTeam(payload.teamId));

    return team;
  } catch (error) {
    console.error(`${PREFIX}/upsertTeamKits error:`, error);
    const message = error instanceof Error ? error.message : String(error);
    return rejectWithValue(message);
  }
});

export const editTeam = createAsyncThunk<Team, UpsertTournamentTeamDto>(
  `${PREFIX}/editTeam`,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const team = { id: payload.id, name: payload.name, shortName: payload.shortName, tournamentId: payload.tournamentId };
      const teamData = await teamsRepository.upsertTeam(team);
      const teamId = teamData.id;

      if (payload.logo instanceof File) {
        const logo: File = Array.isArray(payload.logo) ? payload.logo[0] : payload.logo;
        const res = await teamsResource.uploadLogo(logo, teamId);
        teamData.logo = { id: res.id, url: res.emblem };
      }

      dispatch(invalidateTeam(teamId));

      return teamData;
    } catch (error) {
      console.error(`${PREFIX}/editTeam error:`, error);

      let message;
      if (error instanceof Error) message = error.message;
      else message = String(error);
      return rejectWithValue(message);
    }
  },
);
